import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import Avatar from 'react-avatar';
import Button from '../../../Button';
import { AuthContext } from '../../../../contexts/AuthContext';
import { Theme } from '../../../../interfaces/common';
import { Modules, AuthRoutes } from '../../../../enums';

interface TopbarProps {
  collapsed: boolean;
  onCollapse: (_: boolean) => void;
}

const Topbar: React.FC<TopbarProps> = ({ collapsed, onCollapse }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loggedInUser, theme, setTheme, logout, setLoading } = useContext(AuthContext);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [profileClicked, setProfileClicked] = useState(false);
  const profileRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!profileClicked && !showShortcuts) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileClicked(false);
        setShowShortcuts(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileClicked, showShortcuts]);

  const handleLogoutClick = async () => {
    await logout();
    navigate(Modules.auth);
  };

  const _handleChangePasswordClick = () => {
    navigate(Modules.my + AuthRoutes.changePassword);
  };

  const _handleMyProfileClick = () => {
    navigate(Modules.my + AuthRoutes.profile);
  };

  const handleThemeClick = useCallback((newTheme: Theme) => {
    if (theme === newTheme) return;
    setLoading('true');
    setTheme(newTheme);
  }, [theme, setLoading, setTheme]);

  return (
    <nav
      className="layout-navbar container-fluid navbar navbar-expand-xl navbar-detached align-items-center bg-navbar-theme m-0 px-6 rounded-0"
      id="layout-navbar"
    >
      <div className="navbar-nav-right d-flex align-items-center" id="navbar-collapse">
        <DensityMediumIcon
          className="cursor-pointer ri-24px"
          onClick={() => onCollapse(!collapsed)}
        />
        <ul className="navbar-nav flex-row align-items-center ms-auto">
          <li
            ref={profileRef}
            className="nav-item navbar-dropdown dropdown-user dropdown"
            onClick={() => setProfileClicked((prev) => !prev)}
          >
            <a className={`nav-link dropdown-toggle hide-arrow ${profileClicked && 'show'}`}>
              <div className="d-flex cursor-pointer">
                <div className="flex-shrink-0 me-2">
                  <div className="avatar">
                    {loggedInUser?.profile_image_url ? (
                      <img
                        src={loggedInUser.profile_image_url}
                        alt={loggedInUser?.full_name ?? ''}
                        className="w-px-40 h-px-40 rounded-circle object-fit-cover"
                      />
                    ) : (
                      <Avatar name={loggedInUser?.full_name ?? ''} size="40" maxInitials={2} round />
                    )}
                  </div>
                </div>
                <div className="flex-grow-1">
                  <span className="fw-medium d-block small">{loggedInUser?.full_name ?? ''}</span>
                  <small className="text-muted">
                    {loggedInUser?.role?.name || ''}
                  </small>
                </div>
              </div>
            </a>
            <ul
              className={`dropdown-menu dropdown-menu-end profile-dropdown ${profileClicked && 'show'}`}
              style={{ right: '0px' }}
            >
              {/* <li>
                <Link
                  className="cursor-pointer dropdown-item"
                  text={t('layout.pageTexts.private.topbar.profileDropdown.myProfile')}
                  icon={<i className="ri-user-3-line ri-22px me-3"></i>}
                  textWrapperClassName="align-middle"
                  onClick={handleMyProfileClick}
                />
              </li>

              <li>
                <div className="dropdown-divider"></div>
              </li> */}
              <li>
                <div className="d-flex justify-content-around px-4 py-2">
                  <span
                    className={`cursor-pointer light-dark-theme-selector ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => handleThemeClick('light')}
                  >
                    <i className="ri-sun-line ri-22px"></i>
                  </span>
                  <span
                    className={`cursor-pointer light-dark-theme-selector ${theme === 'dark' ? 'active' : ''}`}
                    onClick={() => handleThemeClick('dark')}
                  >
                    <i className="ri-moon-fill ri-22px"></i>
                  </span>
                </div>
              </li>
              <li>
                <div className="dropdown-divider"></div>
              </li>
              <li>
                <div className="d-grid px-4 pt-2 pb-1">
                  <Button
                    className="btn btn-sm btn-danger d-flex"
                    text={t('layout.pageTexts.private.topbar.profileDropdown.logout')}
                    icon={<i className="ri-logout-box-r-line ms-2 ri-16px"></i>}
                    onClick={handleLogoutClick}
                  />
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Topbar;
